import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';

// Vragen die een zakelijke koper daadwerkelijk stelt voordat hij tekent.
// Eerlijke antwoorden, ook waar die minder goed uitkomen. Een ontwijkend
// antwoord kost hier meer vertrouwen dan het oplevert.
const faqs = [{
  q: 'Wat kost CorePrint?',
  a: 'Het traject bestaat uit twee delen: negentig dagen meten met advertentiebudget, en daarna de bouwfase op basis van de uitkomst. De investering hangt af van de omvang van je markt en het aantal zoekopdrachten dat we moeten testen. In de presentatie rekenen we het concreet door, inclusief het mediabudget dat je aan Google kwijt bent. Dat laatste is geen omzet voor ons, maar het telt wel mee in wat je uitgeeft.'
}, {
  q: 'Wat gebeurt er als uit de data blijkt dat er weinig vraag is?',
  a: 'Dan zeggen we dat, en dan is dat de uitkomst. Het kan gebeuren dat een markt te klein is voor deze aanpak, of dat de vraag ergens anders zit dan verwacht. Dat is vervelend nieuws, maar het is beter om dat na negentig dagen te weten dan na twee jaar SEO-facturen. Het rapport blijft in dat geval gewoon van jou en je kunt zelf beslissen wat je ermee doet.'
}, {
  q: 'Geldt marktexclusiviteit ook voor mij?',
  a: 'Alleen bij het Platinum-pakket. Dan werken we in jouw markt en regio niet ook voor je directe concurrent zolang het traject loopt. Bij Brons, Zilver en Goud geldt die afspraak niet en kunnen we dus wel voor meerdere partijen in dezelfde sector werken. In het kennismakingsgesprek kijken we of jouw markt en regio nog beschikbaar zijn voor een Platinum-traject.'
}, {
  q: 'Wat wordt er van ons verwacht?',
  a: 'Meer dan alleen een handtekening. We hebben aan de start toegang nodig tot je bestaande advertentie- en analysegegevens, en we willen weten wat een klant bij jou gemiddeld waard is. Tijdens het traject leggen we de conversies naast jouw eigen administratie, want een formulierinzending is nog geen opdracht. Reken op een gesprek van een uur per maand.'
}, {
  q: 'Hoe lang duurt het voordat ik resultaat zie?',
  a: 'De advertenties leveren vanaf week twee aanvragen op, want daar betaal je direct voor. Het punt van het traject is echter niet die aanvragen, maar de kennis die eronder ligt. Organische posities beginnen doorgaans na zes tot negen maanden merkbaar te worden, en het volledige effect zie je na twaalf tot achttien maanden. Wie sneller resultaat belooft, verkoopt advertenties en noemt het SEO.'
}, {
  q: 'Van wie is de data na afloop?',
  a: 'Van jou. Het CorePrint-rapport, de campagnegegevens en de opgebouwde content zijn jouw eigendom. Stop je met het traject, dan neem je alles mee. We werken niet met accounts op onze naam waar je geen toegang toe hebt.'
}, {
  q: 'Kunnen we tussentijds stoppen?',
  a: 'De meetfase van negentig dagen is een blok, want een halve meting levert geen bruikbare uitkomst op. Na oplevering van het rapport beslis je of je doorgaat naar de bouwfase. Dat is een bewust beslismoment en geen automatische verlenging.'
}, {
  q: 'Wat is Search Everywhere Optimization?',
  a: 'Zoeken gebeurt niet meer alleen in Google. Een groeiend deel van de zakelijke zoekopdrachten loopt via ChatGPT, Perplexity en de AI-antwoorden binnen Google zelf. Die systemen kiezen andere bronnen dan de klassieke zoekresultaten: ze citeren liever diepgaande, goed gestructureerde pagina\u2019s van herkenbare specialisten. We richten de structuur zo in dat je in beide werelden vindbaar bent.'
}];
function Item({
  q,
  a
}: {
  q: string;
  a: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className="border-b border-[#E8EDF8] last:border-0" data-id="element-98" data-mp="vdqcarhygbo7">
      <button className="w-full text-left py-6 flex items-center justify-between gap-5" onClick={() => setOpen(!open)} aria-expanded={open} data-id="element-99" data-mp="2s7bbnjqcuyxl">
        <span className={`font-semibold text-[1.05rem] transition-colors duration-200 ${open ? 'text-brand-primary' : 'text-brand-dark'}`} data-id="element-100" data-mp="2x88ttwbd7sz3">
          {q}
        </span>
        <motion.div animate={{
        rotate: open ? 180 : 0
      }} transition={{
        duration: 0.2
      }} className="flex-shrink-0" data-id="element-101" data-mp="1wwmygldpmuou">
          <ChevronDownIcon size={22} className={open ? 'text-brand-primary' : 'text-brand-gray'} data-id="element-102" data-mp="26vu8ozp5e97q" />
        </motion.div>
      </button>
      <AnimatePresence initial={false} data-id="element-103" data-mp="1e7gcmphmdmyt">
        {open && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden" data-id="element-104" data-mp="1svb5q6zjhtcf">
            <p className="text-brand-gray leading-relaxed text-[0.98rem] pb-6 max-w-[62ch]" data-id="element-105" data-mp="371obs01lqw3i">
              {a}
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function FAQ() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="py-20 md:py-24 bg-white" data-id="element-106" data-mp="321tnm497okm5">
      <div className="max-w-[820px] mx-auto px-6" data-id="element-107" data-mp="3pua2y71kdyhr">
        <motion.div className="mb-12" initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} data-id="element-108" data-mp="3uslauc8atyxc">
          <div className="section-label" data-id="element-109" data-mp="25cly6l5h1rcy">Vragen vooraf</div>
          <h2 className="text-[clamp(2rem,3.6vw,2.8rem)] font-bold text-brand-dark leading-tight tracking-tight mb-4" data-id="element-110" data-mp="29fqwebiir6vs">
            Wat je wilt weten voordat je begint
          </h2>
          <p className="text-brand-gray text-lg leading-relaxed" data-id="element-111" data-mp="axyizo0ylbdc">
            Ook de vragen waar het antwoord minder gunstig op uitpakt. Je komt er
            toch achter, en dan liever nu.
          </p>
        </motion.div>

        <motion.div className="rounded-2xl border border-[#E8EDF8] bg-white overflow-hidden shadow-soft px-8 md:px-9" initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.15
      }} data-id="element-112" data-mp="16ua961vmmqy">
          {faqs.map(f => <Item key={f.q} q={f.q} a={f.a} data-id="element-113" data-mp="1hl2hlte2spi0" />)}
        </motion.div>

        <motion.div className="mt-14 rounded-2xl border border-[#E8EDF8] bg-[#F8FAFC] p-8 flex flex-col sm:flex-row sm:items-center gap-6" initial={{
        opacity: 0
      }} animate={inView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.5,
        delay: 0.3
      }} data-id="element-114" data-mp="1cmiwfv7b3hqp">
          <p className="text-brand-gray text-[1.02rem] leading-relaxed flex-grow" data-id="element-115" data-mp="1yuy49a6tt2ml">
            Staat jouw vraag er niet bij? Stel hem in de kennismaking. Daar
            krijg je ook de doorrekening voor jouw markt.
          </p>
          <a href="#gesprek" className="inline-flex items-center justify-center bg-brand-primary text-white font-semibold px-7 py-4 rounded-lg hover:opacity-90 transition-opacity duration-200 flex-shrink-0" data-id="element-116" data-mp="168llvqjcrboy">
            Plan een gesprek
          </a>
        </motion.div>
      </div>
    </section>;
}